import { DataMapper, HttpTransport, IResponse } from '@estee/elc-http-transport';
import { IPayload, IRequestTransformerConfig } from '@estee/elc-universal-utils';

import {
    CSRF_TOKEN_COOKIE_NAME,
    genericClientErrorMessageKey,
    genericErrorMessage,
    genericSocialErrorMessage,
    OtpMessageTranslationKey,
    RiskLevels,
    SearchParam,
    signInErrorTransKey,
    socialAlreadyConnectedErrorMessage,
    socialEmailMissingErrorMessage,
    socialMobileCountryCodeNotAllowedErrorMessage,
    socialMobileNumberMissingErrorMessage,
    UNKNOWN_ERROR
} from '~constants/Constants';
import {
    isExpectedSignInErrorCode,
    isExpectedSocialError,
    SocialSignInErrorCode
} from '~constants/SignInErrorCodes';
import { isSocialSignIn, SignInType } from '~constants/SignInTypeEnum';
import { UserIdType } from '~domain/entities/Config';
import { OtpStoreData } from '~domain/entities/OtpStore';
import { IUserFormValues } from '~domain/entities/UserFormStore';
import { getSocialRedirectUri } from '~utils/SocialUtils';

import * as dayjs from 'dayjs';
import { GuestCheckoutError, SignInErrorTranslationKey } from './DataMappings';
import { IP_LOCKED, isIpLock } from './LoadSocialProfileErrorHandler';
import { captureException, tryGetApiError } from './sentryUtils';
import { getValidateAddUserError, isExpectedUserError } from './UserApiErrorHandler';

// User status key returned from API indicates matched user and registration status with given identifiers
export enum UserStatusKey {
    EXISTING_PARTIAL = 'existing.partial',
    EXISTING_PARTIAL_OFFLINE = 'existing.partial.offline',
    EXISTING_COMPLETE = 'existing.complete',
    EXISTING_COMPLETE_OFFLINE = 'existing.complete.offline',
    NEW = 'new',
    NEW_PARTIAL = 'new.partial',
    NEW_PARTIAL_OFFLINE = 'new.partial.offline',
    NEW_COMPLETE = 'new.complete',
    NEW_COMPLETE_OFFLINE = 'new.complete.offline'
}

export enum SocialSignUpType {
    KAKAO_GIFT = 'kakao_gift',
    SSG = 'ssg'
}

export type SocialSignUpTypeKeys = keyof typeof SocialSignUpType;

const socialSignInErrorMessageMap: Map<SocialSignInErrorCode, string> = new Map([
    [SocialSignInErrorCode.PROVIDER_ALREADY_CONNECTED, socialAlreadyConnectedErrorMessage],
    [SocialSignInErrorCode.PROVIDER_MOBILE_MISSING, socialMobileNumberMissingErrorMessage],
    [
        SocialSignInErrorCode.PROVIDER_MOBILE_COUNTRY_CODE_NOT_ALLOWED,
        socialMobileCountryCodeNotAllowedErrorMessage
    ],
    [SocialSignInErrorCode.PROVIDER_EMAIL_MISSING, socialEmailMissingErrorMessage],
    [SocialSignInErrorCode.PROVIDER_MOBILE_INVALID, socialEmailMissingErrorMessage],
    [SocialSignInErrorCode.PROVIDER_INVALID_ACCESS_TOKEN_MISSING, genericErrorMessage],
    [SocialSignInErrorCode.PROVIDER_SERVER_ERROR, genericErrorMessage],
    [SocialSignInErrorCode.PROVIDER_ACCESS_TOKEN_FAILURE_MISSING, genericErrorMessage]
]);

export type PosFrontierProcessResultType = {
    // 1 means success, other codes indicate unsuccessful results depends on feature processed
    resultCode: number;
};

export type PosIKalarProcessResultType = {
    resultMessage: {
        callback_url?: string;
    };
};

export type UserMatchResultType = {
    userId: number | null;
    userIdType?: UserIdType;
    userStatusKey: UserStatusKey;
};

// ELC account matching status by identifier value from social providers
export interface ISocialMatchedUserState extends UserMatchResultType {
    isVerified: boolean;
    // POST session for social always return emailAddress or mobileNumber (no pcEmailAddress)
    userIdType: UserIdType.EMAIL_ADDRESS | UserIdType.MOBILE_NUMBER;
}

export type BitType = 0 | 1;

export interface IUserState extends UserMatchResultType {
    userId: number;
    isVerified?: boolean;
    // unless `userInfoDisplay.userIdentifier.identifier` is `loyaltyGlobalIdentifier`, otherwise this will not be included in the response
    loyaltyGlobalIdentifier?: string;
    loyaltyMembershipNumber?: number;
    userUpdated?: BitType;
    // indicate that identifier fields provided in form/payload conflict with DB values
    identifierConflict?: BitType;
}

export interface ILoadSocialProfileResponse {
    // E.164 format
    mobileNumber?: string;
    // always be there even user chooses to not share, API returns empty string in this case
    emailAddress: string;
    [key: string]: string | number | boolean | undefined;
}

// init Session/CSRF cookies, and check sign in status, https://confluence.esteeonline.com/pages/viewpage.action?pageId=285825756
export interface IGetSessionResponse {
    userId: number | null;
    signedIn: boolean;
    // already signed in as guest
    isGuestCheckout: boolean;
}

export interface IDeleteSessionResponse {
    success: BitType;
}

export interface IUserApiResponse {
    features:
        | {
              posFrontiers?: PosFrontierProcessResultType;
              posIKalar?: PosIKalarProcessResultType;
          }
        | Record<string, never>;
    users: IUserState[];
}

export interface IFeatureProcessResults {
    posFrontiersErrorKey?: string;
    posIKalarCallbackUrl?: string;
}

export interface IAddUserResponse extends IFeatureProcessResults {
    users: IUserState[];
    success?: boolean;
}

export type validateAddUserErrorType =
    | typeof genericErrorMessage
    | typeof genericClientErrorMessageKey;

// Note if both identifiers won't match to existing ELC account, response is []
// This is different from the normal(actual) POST /user (create account) request
// Or only contains existing.* record
export interface IValidateAddUserResponse {
    users: IUserState[];
    error?: validateAddUserErrorType;
}

export interface IPostAuthCodeApiPayload {
    authCode: string;
    state: string;
    socialProvider: SignInType;
    providerSource?: SocialSignUpType;
}

export interface IPostSocialSignUpSessionApiRequest {
    [key: string]: string;
    socialProvider: SocialSignUpType;
}

// TODO: APACRIGEL-2052 separate responses for success / showConsent / error
export interface IPostAuthCodeResponse {
    showConsent?: number;
    users?: ISocialMatchedUserState[];
    errorCode?: string;
    error?: string;
    success: number;
    forwardToCart?: BitType;
}

export type PostSessionResponse = Omit<IPostAuthCodeResponse, 'showConsent'>;

export interface IUpdateUserResponse extends IFeatureProcessResults {
    user: IUserState;
}

export interface IUserSignInApiPayload {
    [key: string]: string | number;
}

enum UserState {
    GUEST = 'guest',
    REGISTERED = 'registered',
    SIGNED_IN = 'signedIn',
    UNKNOWN = 'unknown'
}

interface IPerlgemSessionData {
    signedInTimeRemaining?: number;
    userState?: UserState;
}

const PERLGEM_SESSION_KEY = 'perlgem_session';

// Data structure of raw API response
export interface IUserSignInApiResponse {
    errors?: [{ key: string }];
    success?: number;
    userId?: number;
    rs?: RiskLevels;
    hmac?: string;
    forwardToCart?: BitType;
    userState?: UserState;
    signedInTimeRemaining?: number;
}

export interface IUserSignInResponse {
    userId?: number;
    error: SignInErrorTranslationKey | GuestCheckoutError | '';
    errorCode?: string;
    riskLevel?: RiskLevels;
    hmac?: string;
    forwardToCart?: BitType;
}

export interface IRampPgApiConfig {
    regionId: number;
    brandId: number;
    token: string;
    baseUrl: string;
    businessUnitId: string;
}

enum RequestHeader {
    BUID = 'business-unit-id',
    BU_CONFIG_NODE = 'business-unit-config-node'
}

enum MockRequestHeader {
    API_KEY = 'x-api-key',
    MOCK_MATCH_REQUEST_HEADERS = 'x-mock-match-request-headers'
}

export const OTP_API_PATH = '/rest/api/v1/user/mfa';
const RESET_PASSWORD_API_PATH = '/rest/api/v1/user/reset-password'; // TODO: get correct url from BE

export type OtpEmail = {
    emailAddress: string;
};
export type OtpSms = {
    mobileNumber: string;
};

export type OtpIdentifier = OtpEmail | OtpSms;

type RequestAction = {
    doRequest<T>(payload: IPayload): Promise<IResponse<T>>;
};

export type GenerateOtpRequest = {
    hmac?: string;
} & OtpIdentifier;

export type VerifyOtpRequest = {
    requestId?: string;
    nonce?: string;
    verifyCode: string;
    hmac?: string;
} & OtpIdentifier;

export type OtpRequest = (GenerateOtpRequest | VerifyOtpRequest) & RequestAction;

export interface IOtpApiResponse {
    mfaStatus?: string;
    requestId?: string;
    nonce?: string;
    resendWaitingPeriod?: number;
}

export interface IApiError {
    errors?: [{ key: string }];
}

export type OtpResult = Omit<OtpStoreData, 'showOtp' | 'type' | 'flow'> & {
    success: boolean;
    rs?: RiskLevels;
};

export interface ISavePasswordAndSignInBody {
    emailAddress: string;
    password: string;
}

export interface ISavePasswordAndSignInResponse {
    success: boolean;
    transKey?: string;
}

export const STATIC_LOOKUP_PATTERN = /lookups\/.*\.json/;
export const PUBLIC_API_PATTERN = /\/rest\/api\/v1\/rc\/util\/postalcodeinfo/;

// TODO: https://confluence.esteeonline.com/display/ELP/%5BSD%5D+Stardust+Frontend+Service+Architecture+Playbook#id-[SD]StardustFrontendServiceArchitecturePlaybook-APISDK, should accept JSON instead of model
export class RampPgApiSdk {
    httpTransport: HttpTransport;
    userPath = '/rest/api/v1/user';
    sessionPath = '/rest/api/v1/user/session';
    searchParams = new Map<string, string>();
    dataMapper: DataMapper;
    businessUnitId: string;
    businessUnitConfigNode: string;
    useMockServer: boolean;
    useProxyServer: boolean;
    proxyServerUrl: string;
    brandId: number;
    regionId: number;

    constructor(httpTransport: HttpTransport, dataMapper: DataMapper) {
        this.httpTransport = httpTransport;
        this.dataMapper = dataMapper;
    }

    private setupDev = () => {
        const mockServerUrl = process.env.MOCK_SERVER_URL ?? '';
        const xApiKey = process.env.MOCK_API_KEY ?? '';
        this.httpTransport.setBaseUrl(mockServerUrl);
        this.httpTransport.addRequestTransformer(async (config: IRequestTransformerConfig) => {
            const { headers, url, method } = config as {
                headers: Headers;
                url: string;
                method: string;
            };

            if (!STATIC_LOOKUP_PATTERN.test(url)) {
                // For example, mock-post-user, mock-put-user, mock-post-user-session
                const mockMatchRequestHeader = [
                    'mock',
                    method?.toLowerCase(),
                    new URL(url).pathname
                        .replace(/\/rest\/api\/v1\/user(?:\/\d+)?/, 'user')
                        .replace(/\//g, '-')
                ].join('-');

                headers.set(MockRequestHeader.API_KEY, xApiKey);
                headers.set(MockRequestHeader.MOCK_MATCH_REQUEST_HEADERS, mockMatchRequestHeader);
                headers.set(mockMatchRequestHeader, this.getCookie(mockMatchRequestHeader) ?? '');
            }
        });
    };

    private getCookie = (name: string) => {
        const cookies = `; ${document.cookie}`;
        const parts = cookies.split(`; ${name}=`);
        if (parts.length === 2) {
            return decodeURIComponent(parts.pop()?.split(';').shift() || '');
        } else {
            return null;
        }
    };

    public setup(config: IRampPgApiConfig): void {
        const { baseUrl, token, regionId, brandId, businessUnitId } = config;
        this.brandId = brandId;
        this.regionId = regionId;
        this.searchParams.set(SearchParam.REGION, `${regionId}`);
        this.searchParams.set(SearchParam.BRAND, `${brandId}`);
        this.businessUnitId = businessUnitId;
        this.httpTransport.setAuthFailureHandlingEnabled(false);

        if (process.env.NODE_ENV === 'development' && process.env.USE_MOCK_SERVER === 'true') {
            this.setupDev();

            return;
        }

        // Used to check the endpoints which need BUID headers, i.e. /user and /user/:id
        const userPathRegex = new RegExp(`^${this.userPath}(?:\\/\\d+)?$`);
        this.httpTransport.setBaseUrl(baseUrl);
        this.httpTransport.addRequestTransformer(
            async (transformConfig: IRequestTransformerConfig) => {
                const { headers, url } = transformConfig as { headers: Headers; url: string };

                if (!PUBLIC_API_PATTERN.test(url)) {
                    headers.set('AuthorizationCredentials', `Basic ${token}`);
                }

                if (userPathRegex.test(new URL(url).pathname)) {
                    headers.set(RequestHeader.BUID, this.businessUnitId);
                    headers.set(RequestHeader.BU_CONFIG_NODE, this.businessUnitConfigNode);
                }
            }
        );
    }

    public setBusinessUnitConfigNode = (configName: string) => {
        this.businessUnitConfigNode = configName;
    };

    private getRequestUrl = (
        resourcePaths: (string | number)[] = [],
        extraSearchParams: Map<string, string> = new Map<string, string>()
    ) => {
        const urlSearchParams = new URLSearchParams([
            ...this.searchParams.entries(),
            ...extraSearchParams.entries()
        ]);

        return `${resourcePaths.filter((path) => !!path).join('/')}?${urlSearchParams}`;
    };

    public loadSocialProfile = async (provider: string): Promise<ILoadSocialProfileResponse> => {
        try {
            const response = await this.httpTransport.get({
                url: this.getRequestUrl([this.sessionPath, 'providerinfo', provider])
            });

            return response.data as ILoadSocialProfileResponse;
        } catch (e) {
            const captured = captureException(e, { exclude: isIpLock });
            throw !captured ? new Error(IP_LOCKED) : e;
        }
    };

    private postUser = async <T>(requestUrl: string, data: IUserFormValues): Promise<T> => {
        const body = this.dataMapper.mapRequest('addUser', data);
        try {
            const payload = {
                url: requestUrl,
                body
            };
            const definedPayload = this.removeEmptyValueFromPayload(payload);
            const response = await this.httpTransport.post(definedPayload);
            const result = this.dataMapper.mapResponse('addUser', response.data as T) as T;

            return result;
        } catch (e) {
            captureException(e, { extra: body, exclude: isExpectedUserError });
            throw e;
        }
    };

    public validateAddUser = async (data: IUserFormValues): Promise<IValidateAddUserResponse> => {
        const requestUrl = this.getRequestUrl(
            [this.userPath],
            new Map([['mode', 'validate_only']])
        );
        try {
            return await this.postUser<IValidateAddUserResponse>(requestUrl, data);
        } catch (e) {
            const { key, message } = tryGetApiError(e) || {
                key: '',
                message: ''
            };

            return { error: getValidateAddUserError(key, message), users: [] };
        }
    };

    // TODO: APACRIGEL-2052 use validateAddUser separately for validate_only mode
    public addUser = async (
        data: IUserFormValues,
        needValidate?: boolean
    ): Promise<IAddUserResponse> => {
        const requestUrl = needValidate
            ? this.getRequestUrl([this.userPath], new Map([['mode', 'validate_only']]))
            : this.getRequestUrl([this.userPath]);
        try {
            const result = await this.postUser<IAddUserResponse>(requestUrl, data);

            return { ...result, success: true };
        } catch {
            return { users: [] as IUserState[], success: false };
        }
    };

    private putUser = async (
        userId: number,
        data: IUserFormValues
    ): Promise<IUpdateUserResponse | Record<string, never>> => {
        const requestUrl = this.getRequestUrl([this.userPath, userId]);
        const body = this.dataMapper.mapRequest('updateUser', data);
        try {
            const payload = {
                url: requestUrl,
                body
            };

            const response = await this.httpTransport.put(payload);

            return this.dataMapper.mapResponse('updateUser', response.data as IUserApiResponse);
        } catch (e) {
            captureException(e, { extra: body, exclude: isExpectedUserError });
            throw e;
        }
    };

    public updateUser = async (
        userId: number,
        data: IUserFormValues
    ): Promise<IUpdateUserResponse | Record<string, never>> => {
        try {
            return await this.putUser(userId, data);
        } catch {
            return {};
        }
    };

    public patchPassword = async (userId: number, password: string) => {
        const requestUrl = this.getRequestUrl([this.userPath, userId]);
        try {
            const payload = {
                url: requestUrl,
                body: { password }
            };

            const response = await this.httpTransport.patch(payload);

            const {
                user: { userStatusKey }
            } = this.dataMapper.mapResponse(
                'patchPassword',
                response.data as IUserApiResponse
            ) as IUpdateUserResponse;

            return [
                UserStatusKey.EXISTING_COMPLETE_OFFLINE,
                UserStatusKey.EXISTING_COMPLETE
            ].includes(userStatusKey);
        } catch {
            return false;
        }
    };

    public getSession = async (): Promise<IGetSessionResponse> => {
        const requestUrl = this.getRequestUrl([this.sessionPath]);

        try {
            const { data } = await this.httpTransport.get({
                url: requestUrl
            });

            const { signedInTimeRemaining, userState } = data;
            this.hydratePerlgemSessionCookie({ signedInTimeRemaining, userState });

            return data as IGetSessionResponse;
        } catch (e) {
            captureException(e);
            throw e;
        }
    };

    public deleteSession = async (): Promise<boolean> => {
        const csrfToken = this.getCookie(CSRF_TOKEN_COOKIE_NAME);
        if (!csrfToken) {
            return false;
        }

        try {
            const url = this.getRequestUrl([this.sessionPath]);
            // Response will contain new session cookie
            const response = await this.httpTransport.delete<IDeleteSessionResponse>({
                url,
                headers: { 'X-CSRF-Token': csrfToken }
            });

            return response?.data?.success === 1;
        } catch (e) {
            captureException(e);

            return false;
        }
    };

    public signIn = async (
        data: IUserSignInApiPayload,
        signInType?: SignInType
    ): Promise<IUserSignInResponse> => {
        const requestUrl = this.getRequestUrl([
            this.sessionPath,
            isSocialSignIn(signInType) ? signInType : ''
        ]);
        const csrfToken = this.getCookie(CSRF_TOKEN_COOKIE_NAME);

        try {
            const payload = {
                url: requestUrl,
                body: {
                    ...data,
                    csrfToken
                },
                options: {
                    credentials: 'include'
                }
            };
            const response = await this.httpTransport.post(payload);
            const apiResponse = response.data as IUserSignInApiResponse;
            const {
                hmac,
                forwardToCart,
                rs: riskLevel,
                signedInTimeRemaining,
                success,
                userId,
                userState
            } = apiResponse || {};

            this.hydratePerlgemSessionCookie({ signedInTimeRemaining, userState });

            return {
                userId,
                riskLevel,
                hmac,
                error: success ? '' : signInErrorTransKey,
                forwardToCart
            };
        } catch (e) {
            const dataMapperMethodName =
                signInType === SignInType.GUEST ? 'fromGuestCheckoutError' : 'fromSignInError';

            const errorCode: string = tryGetApiError(e)?.key ?? UNKNOWN_ERROR;

            captureException(e, { extra: data, exclude: isExpectedSignInErrorCode });

            return {
                errorCode,
                error: this.dataMapper.mapResponse(
                    dataMapperMethodName,
                    e.errorResponse?.data
                ) as SignInErrorTranslationKey
            };
        }
    };

    private getDate = () => {
        const dateFormat = 'YYYYMMDD';

        return dayjs().format(dateFormat);
    };

    public loadStaticJsonContent = async (url: string) => {
        try {
            const payload = {
                url: `${url}?t=${this.getDate()}`,
                options: {
                    redirect: 'error'
                }
            };
            const response = await this.httpTransport.get(payload);

            return response.data || [];
        } catch {
            return [];
        }
    };

    public loadSuggestionApiJsonContent = async (url: string, brandRegionQuery: boolean = true) => {
        try {
            const payload = {
                url: brandRegionQuery ? this.getRequestUrl([url]) : url,
                options: {
                    credentials: 'include'
                }
            };
            const response = await this.httpTransport.get(payload);

            const result = response.data || [];
            if (!Array.isArray(result)) {
                return result?.suggestions;
            }

            return result;
        } catch {
            return [];
        }
    };

    public postAuthCode = async (
        params: IPostAuthCodeApiPayload
    ): Promise<IPostAuthCodeResponse> => {
        const { authCode, state, socialProvider, providerSource } = params;
        const url = this.getRequestUrl([this.sessionPath, socialProvider]);
        try {
            const csrfToken = this.getCookie(CSRF_TOKEN_COOKIE_NAME);
            const body = this.removeEmptyValueFromPayload({
                auth_code: authCode,
                state,
                csrfToken,
                redirect_uri: getSocialRedirectUri(),
                providerSource,
                acceptPrivacyPolicyAndTnc: 1
            });

            const payload = {
                url,
                body
            };
            const response = await this.httpTransport.post(payload);

            return response.data as IPostAuthCodeResponse;
        } catch (e) {
            const errorCode: SocialSignInErrorCode | undefined =
                e.errorResponse?.data?.errors?.[0]?.key;
            let errorTranslationKey;
            if (errorCode && socialSignInErrorMessageMap.has(errorCode)) {
                errorTranslationKey = socialSignInErrorMessageMap.get(errorCode);
            }

            captureException(e, {
                extra: { authCode, state, socialProvider, providerSource },
                exclude: isExpectedSocialError
            });

            return {
                errorCode: errorCode ?? UNKNOWN_ERROR,
                error: errorTranslationKey ?? genericSocialErrorMessage,
                success: 0
            };
        }
    };

    // This API call is the same as `postAuthCode` in OAuth flow,
    // which receives essential information to access/obtain user profile from
    // 3rd party social provider API, and returns ELC account matching status
    // base on the email/mobile got from 3rd party.
    public postSocialSignUpSession = async (
        params: IPostSocialSignUpSessionApiRequest
    ): Promise<PostSessionResponse> => {
        const { socialProvider, ...rest } = params;
        const requestUrl = this.getRequestUrl([this.sessionPath, socialProvider]);
        try {
            const payload = {
                url: requestUrl,
                body: {
                    providerParams: { ...rest }
                }
            };
            const response = await this.httpTransport.post(payload);

            return { ...(response.data as PostSessionResponse), success: 1 };
        } catch (e) {
            captureException(e, { extra: params, exclude: isExpectedSocialError });

            return { success: 0 };
        }
    };

    public disconnectSocial = async (socialProvider: SignInType): Promise<string | undefined> => {
        const requestUrl = this.getRequestUrl([this.userPath, 'socialinfo', socialProvider]);
        try {
            const payload = {
                url: requestUrl
            };
            const response = await this.httpTransport.delete(payload);
            const { success } = response.data as IUserSignInApiResponse;

            return success === 1 ? '' : genericSocialErrorMessage;
        } catch (e) {
            return genericSocialErrorMessage;
        }
    };

    public otpRequest = async <T extends OtpRequest>(
        request: T,
        url: string
    ): Promise<OtpResult | undefined> => {
        const requestUrl = this.getRequestUrl([url]);
        const csrfToken = this.getCookie(CSRF_TOKEN_COOKIE_NAME);

        try {
            const payload = {
                url: requestUrl,
                body: {
                    ...request,
                    csrfToken
                }
            };

            const response = await request.doRequest(payload);
            const data = response.data as IOtpApiResponse;
            const { requestId, nonce, resendWaitingPeriod } = data || {};
            const result = this.dataMapper.mapResponse('fromOtp', data);

            return {
                requestId,
                resendWaitingPeriod,
                nonce,
                translationKey: result as OtpMessageTranslationKey,
                success: true
            };
        } catch (e) {
            const { errors, rs } = e?.errorResponse?.data || {};

            if (errors?.length) {
                const result = this.dataMapper.mapResponse('fromOtpError', { errors });

                return { translationKey: result as OtpMessageTranslationKey, success: false, rs };
            } else {
                return { translationKey: genericErrorMessage, success: false };
            }
        }
    };

    public savePasswordAndSignIn = async (
        body: ISavePasswordAndSignInBody
    ): Promise<ISavePasswordAndSignInResponse> => {
        const requestUrl = this.getRequestUrl([RESET_PASSWORD_API_PATH]);

        try {
            const payload = {
                url: requestUrl,
                body
            };

            await this.httpTransport.patch(payload);

            return { success: true };
        } catch {
            // TODO: after BE is ready get list of possible error keys
            return { transKey: genericErrorMessage, success: false };
        }
    };

    private removeEmptyValueFromPayload = (data: unknown) => JSON.parse(JSON.stringify(data));

    private hydratePerlgemSessionCookie = ({
        signedInTimeRemaining = 0,
        userState = UserState.UNKNOWN
    }: IPerlgemSessionData): void => {
        const perlgemSession = JSON.stringify({ signedInTimeRemaining, userState });
        document.cookie = `${PERLGEM_SESSION_KEY}=${encodeURIComponent(perlgemSession)}`;
    };
}
